import { Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { getUser } from "@/lib/api";

/**
 * Role → allowed route keys.
 * Frontend gate only (backend must also enforce via require_roles).
 *
 * 04/08/2026 — Post-restructure roles:
 * • Atendimento owns the lead until the RT is scheduled
 * • Consultor de Cuidados evaluates needs + presents the Proposta Final
 *   (RT-scheduled → deposit paid)
 * • Gestor de Cliente + Equipa Técnica take over after deposit paid
 */
export const ROLE_ROUTES = {
  admin: "*", // wildcard
  // 25/02/2026 — Painel "Cauções" (chave `deposits`) disponível a TODOS
  // os utilizadores autenticados (pedido do utilizador). Dá visibilidade
  // cruzada do estado das cauções antes de accionar clientes.
  atendimento: ["dashboard", "leads", "lead-new", "lead-detail", "profile", "daily-report", "office-report", "help", "offices", "my-bonuses", "performance", "alerts", "rts-calendar", "deposits"],
  consultor:   ["dashboard", "leads", "lead-detail", "technical", "profile", "daily-report", "office-report", "help", "offices", "my-bonuses", "performance", "alerts", "rts-pendentes", "rts-calendar", "deposits", "margins"],
  gestor:      ["dashboard", "leads", "lead-detail", "technical", "profile", "daily-report", "office-report", "help", "finance-daf", "care-plans", "offices", "my-bonuses", "performance", "alerts", "rts-pendentes", "rts-calendar", "deposits", "margins"],
  direcao: ["dashboard", "leads", "lead-detail", "profile", "daily-report", "office-report", "help", "finance-daf", "care-plans", "offices", "bonuses-overview", "my-bonuses", "performance", "alerts", "rts-pendentes", "rts-calendar", "deposits", "margins"], // read-only
  dgo: ["dashboard", "leads", "lead-detail", "profile", "daily-report", "office-report", "help", "finance-daf", "care-plans", "offices", "bonuses-overview", "my-bonuses", "performance", "alerts", "rts-pendentes", "rts-calendar", "deposits", "margins"],
  daf: ["dashboard", "profile", "help", "finance-daf", "rts-calendar", "deposits", "office-report"],
  recrutamento: ["dashboard", "profile", "daily-report", "office-report", "help", "rts-calendar", "deposits"],
};

/** Check if the current user has access to a route key. */
export function canAccess(routeKey, user = null) {
  const u = user || getUser();
  if (!u) return false;
  const allowed = ROLE_ROUTES[u.role];
  if (!allowed) return false;
  if (allowed === "*") return true;
  return allowed.includes(routeKey);
}

/** Wraps a route element to gate by role. Redirects to /dashboard if denied. */
export function RoleGate({ routeKey, children }) {
  if (canAccess(routeKey)) return children;
  return <Navigate to="/dashboard" replace />;
}

/** Which actions a role can perform. Used to hide buttons across screens. */
export const ROLE_ACTIONS = {
  admin: "*",
  atendimento: ["create_lead", "generate_estimate", "schedule_qt", "edit_qi", "zoho_sync", "assign_consultor", "assign_gestor"],
  // Consultor de Cuidados: RT scheduling through deposit paid.
  consultor:   ["schedule_qt", "run_qt", "generate_proposal", "attach_pdf", "zoho_sync", "generate_estimate"],
  gestor:      ["schedule_qt", "run_qt", "generate_proposal", "attach_pdf", "zoho_sync", "generate_estimate", "assign_gestor"],
  direcao: ["daf_reconcile"],
  dgo: ["daf_reconcile"],
  daf: ["daf_reconcile", "assign_gestor"],
  recrutamento: [],
};

export function canDo(action, user = null) {
  const u = user || getUser();
  if (!u) return false;
  const allowed = ROLE_ACTIONS[u.role];
  if (!allowed) return false;
  if (allowed === "*") return true;
  return allowed.includes(action);
}

// ────────────────────────── SECTION PERMISSIONS ──────────────────────────
// 05/02/2026 — Matriz por role para 4 secções críticas do LeadDetail.
// Backend fonte da verdade: `GET /api/me/permissions`. Front cacheia em
// memória por sessão (o role da pessoa autenticada não muda em runtime).
// Níveis: "none" | "view" | "edit". "edit" implica "view".

export const SECTION_LEVELS = ["none", "view", "edit"];
const LEVEL_ORDER = { none: 0, view: 1, edit: 2 };

let _permCache = null;
let _permPromise = null;

export async function fetchMyPermissions(force = false) {
  if (!force && _permCache) return _permCache;
  if (_permPromise) return _permPromise;
  _permPromise = api.get("/me/permissions")
    .then((r) => {
      _permCache = r.data?.permissions || {};
      return _permCache;
    })
    .catch((e) => {
      console.warn("[permissions] fetch failed:", e?.message);
      _permCache = {};
      return _permCache;
    })
    .finally(() => { _permPromise = null; });
  return _permPromise;
}

export function invalidatePermissionsCache() {
  _permCache = null;
  _permPromise = null;
}

/** React hook: returns {perms, ready, can}. Auto-loads once per session. */
export function useSectionPermissions() {
  const [perms, setPerms] = useState(_permCache);
  const [ready, setReady] = useState(!!_permCache);
  useEffect(() => {
    let alive = true;
    if (!_permCache) {
      fetchMyPermissions().then((p) => {
        if (alive) { setPerms(p); setReady(true); }
      });
    }
    return () => { alive = false; };
  }, []);
  const canView = (section) => (LEVEL_ORDER[(perms || {})[section]] ?? 0) >= 1;
  const canEdit = (section) => (LEVEL_ORDER[(perms || {})[section]] ?? 0) >= 2;
  const levelOf = (section) => (perms || {})[section] || "none";
  return { perms: perms || {}, ready, canView, canEdit, levelOf };
}
